import { Component, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { ModalController, ToastController } from "@ionic/angular";
import { isNil } from "lodash";
import { environment } from "src/environments/environment";
import { PrincipleItemModelDto } from "../performance-cycles/configuration/cycle-partner-configuration/PrincipleItemModelDto";
import { BaseClass } from "../shared/classes/base";
import { UserRoleEnum } from "../shared/enums/UserRoleEnum";
import { HandleYouTubeUrl } from "../shared/helper/embedUrlHelper";
import { CyclePreview } from "../shared/interfaces/cycle.interface";
import { GoalProgressPreview } from "../shared/interfaces/goal-progress.interface";
import { ProcessPreview, ProcessStatusEnum, ProcessTypeEnum } from "../shared/interfaces/process.interface";
import { SelectCycleComponent } from "../shared/modals/select-cycle/select-cycle.component";
import { AuthService, UserItem } from "../shared/services/auth.service";
import { CycleService } from "../shared/services/cycle.service";
import { LogoService } from "../shared/services/logo.service";
import { TranslationService } from "../shared/services/translation.service";
import { UtilService } from "../shared/services/util.service";
import { AttributeItem } from "../users/_interfaces/users-admin";
import { CorePrincipleModel } from "./core-principles/model/core-principle.model";
import { DashboardService } from "./dashboard.service";
import { FeedbackStatusComponent } from "./feedback-status/feedback-status.component";
import { FreeEvaluationSummary } from "./interfaces/free-evaluation-summary.interface";
import { applyCachedExternalClientStyles, applyExternalClientStyles, ExternalClientsStylesCacheKey } from '../shared/services/external-client.service';
import { ExternalClientStylesService } from '../external-client-styles/external-client-styles.service';
import { AttributesService } from '../processes/attributes.service';
import { AssetService } from "../shared/services/multi-clients/asset.service";
import { ConfigService } from "../shared/services/multi-clients/config.service";

@Component({
    selector: "app-dashboard",
    templateUrl: "./dashboard.page.html",
    styleUrls: ["./dashboard.scss"],
})
export class DashboardPage extends BaseClass implements OnInit {
    @ViewChild(FeedbackStatusComponent, { static: true }) feedbackStatusComponent: FeedbackStatusComponent;

    public environment = null;
    public hideReports = false;
    public showCorePrinciples = false;
    public showDashboardWelcome = false;
    public hideVideoInstructionsLabel = true;

    public cycles: Array<CyclePreview> = [];
    public selectedCycle: CyclePreview;
    public selectedCycleId: number;
    public firstSearchDone: boolean = false;
    public processes: Array<ProcessPreview> = [];
    public nextProcess: ProcessPreview;
    public goalsProgress: Array<GoalProgressPreview> = [];
    public userInfo: UserItem;
    public freeEvaluationSummary: FreeEvaluationSummary;
    public userAttributes: Array<AttributeItem>;
    public videoUrl: string;
    public currentLanguage: string = "PtBr";
    public isHrOrAdmin: boolean = false;
    public hasReportAccess: boolean = false;
    public welcomeMessage: string;
    public tutorialPath: string = 'assets/documents/tutorial.pdf';

    //cycle principles
    public corePrinciplesAllLanguages: PrincipleItemModelDto[] = [];
    public leadershipPrinciplesAllLanguages: PrincipleItemModelDto[] = [];
    public corePrinciples: Array<CorePrincipleModel> = [];
    public leadershipPrinciples: Array<CorePrincipleModel> = [];
    public loadedClycle: boolean = false;
    private _calibrationProcessId: number;

    // Novas variáveis públicas
    public hrChannel: boolean = false;
    public tutorialMenu: boolean = false;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _dashboardService: DashboardService,
        private _router: Router,
        private _utilService: UtilService,
        private _authService: AuthService,
        private _cycleService: CycleService,
        private _logoService: LogoService,
        private _assetService: AssetService,
        private _configService: ConfigService,
        private _modalController: ModalController,
        private _externalClientStylesService: ExternalClientStylesService,
        private _attributeService: AttributesService,
    ) {
        super(_toastController, _translationService);
        this._utilService.removeProcessIdFromLocalStorage();
        _translationService.languageChanged$.subscribe((lang) => {
            this._updateProcessDates();
            this.fillLanguage(lang);
            this.loadVideoUrl(true);
            this.fillPartnerPrinciples();
            this.fillWelcomeMessage();
            this._loadPrinciples();
        });
        this.isHrOrAdmin = this._authService.hasRole(UserRoleEnum.HR) || this._authService.hasRole(UserRoleEnum.Administrator);
        this.hasReportAccess = this._authService.hasRole(UserRoleEnum.ReportAccess);

        let externalClientStyles = localStorage.getItem(ExternalClientsStylesCacheKey);
        if (externalClientStyles)
            this.initCachedExternalClientStyles();
    }

    ngOnInit(): void {
        const lang = this._translationService.getSelectedLanguage();
        this.fillLanguage(lang);
        if (this.corePrinciples.length === 0 || this.leadershipPrinciples.length === 0) {
            this._loadPrinciples();
        }
        this._loadEnvironments();
        this._assetService.getTutorialPdfPath().then(path => {
            this.tutorialPath = path;
        }); 
    }

    private _loadEnvironments() {
        this.environment = this._configService.settings;
        this.hideReports = this._configService.settings.hideReports;
        this.showCorePrinciples = this._configService.settings.features.showCorePrinciples;
        this.showDashboardWelcome = this._configService.settings.features.showDashboardWelcome;
        this.hideVideoInstructionsLabel = !this._configService.settings.features.hideVideoInstructionsLabel;
        // Inicializa variáveis para evitar acesso direto ao environment no HTML
        this.hrChannel = this._configService.settings.features.hrChannel;
        this.tutorialMenu = this._configService.settings.features.tutorialMenu;
    }

    fillLanguage(lang: string) {
        switch (lang) {
            case "pt-BR":
                this.currentLanguage = "PtBr";
                break;
            case "es-ES":
                this.currentLanguage = "EsEs";
                break;
            case "en-US":
                this.currentLanguage = "EnUs";
                break;
            default:
                this.currentLanguage = "PtBr";
        }
    }

    ionViewWillEnter() {
        this.initExternalClientStyles();
        this._loadUserInfo();
        this.feedbackStatusComponent.getFeedback();
        this.loadVideoUrl();
    }

    private verifyCycleAttribs() {
        if (!this.selectedCycle) {
            return;
        }

        if (this.selectedCycle.isCyclePartner) {
            console.log('removendo')
            this.corePrinciples = [];
            this.leadershipPrinciples = [];
            const params = this.selectedCycle.partnerParams;
            this._logoService.Logo = params.logoUrl;
            this._loadPartnerPrinciples(this.selectedCycleId);
        }

        this.fillWelcomeMessage();
        this.loadVideoUrl();
    }

    private fillWelcomeMessage() {
        if (!this.selectedCycle) return;

        if (this.selectedCycle.isCyclePartner) {
            if (this.selectedCycle.partnerParams) this.welcomeMessage = this.selectedCycle.partnerParams[`welcome${this.currentLanguage}`];
            return;
        }

        this.welcomeMessage = this._translationService.getTranslation("dashboard.welcome");
        if (this.welcomeMessage === "dashboard.welcome") this.welcomeMessage = null;
    }

    private loadVideoUrl(languageChange = false) {
        if (!this.selectedCycle) return;

        if (this.selectedCycle.isCyclePartner) {
            const params = this.selectedCycle.partnerParams;
            const videoFromCycle = HandleYouTubeUrl(params[`urlVideo${this.currentLanguage}`]);

            if (`${videoFromCycle}`.trim().length == 0) {
                this.videoUrl = null;
            } else {
                this.videoUrl = videoFromCycle;
            }
            return;
        }

        if (this.videoUrl && !languageChange) return;

        this.videoUrl = this._translationService.getTranslation("dashboard.videoUrl");
        if (this.videoUrl === "dashboard.videoUrl") this.videoUrl = null;
    }

    public viewTeamProgress(): void {
        this._router.navigate(["performance-cycles"]);
    }

    public viewReports(): void {
        this._router.navigate(["reports"]);
    }

    public viewNotepad(): void {
        this._router.navigate(["notepad"]);
    }

    public viewHrChannel(): void {
        this._router.navigate(["hr-channel"]);
    }

    public triggerProcessAction(processType: ProcessTypeEnum, cycleId: number, cycleProcessConfigId?: number): void {
        switch (processType) {
            case ProcessTypeEnum.GoalInputs:
                this._router.navigate(["goals-input/" + cycleId], { state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.GoalInputs).processId } });
                break;
            case ProcessTypeEnum.GoalInputValidation:
                this._router.navigate(["goals-validation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.GoalInputValidation).processId },
                });
                break;
            case ProcessTypeEnum.FreeEvaluationMidYear:
                this._router.navigate(["free-evaluation-mid-year/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.FreeEvaluationMidYear).processId },
                });
                break;
            case ProcessTypeEnum.FreeEvaluationEndYear:
                this._router.navigate(["free-evaluation-end-year/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.FreeEvaluationEndYear).processId },
                });
                break;
            case ProcessTypeEnum.GoalsReview:
                this._router.navigate(["goals-review/" + cycleId], { state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.GoalsReview).processId } });
                break;
            case ProcessTypeEnum.GoalsReviewValidation:
                this._router.navigate(["goals-review-validation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.GoalsReviewValidation).processId },
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluatorsSelection:
                this._router.navigate(["360-evaluation/selection/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.ThreeSixtyEvaluatorsSelection).processId },
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluatorsValidation:
                this._router.navigate(["360-evaluation/validation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.ThreeSixtyEvaluatorsValidation).processId },
                });
                break;
            case ProcessTypeEnum.ThreeSixtyEvaluation:
                const stateUrl = this._utilService.setUrlStateGuard(ProcessTypeEnum.ThreeSixtyEvaluation, this.processes);
                this._router.navigate(["360-evaluation/evaluation/" + cycleId], { state: stateUrl });
                break;
            case ProcessTypeEnum.SelfEvaluationMidYear:
                this._router.navigate(["self-evaluation-mid-year/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.SelfEvaluationMidYear).processId },
                });
                break;
            case ProcessTypeEnum.SelfEvaluation:
                this._router.navigate(["self-evaluation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.SelfEvaluation).processId },
                });
                break;
            case ProcessTypeEnum.ManagerEvaluation:
                this._router.navigate(["manager-evaluation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.ManagerEvaluation).processId },
                });
                break;
            case ProcessTypeEnum.HeadEvaluation:
                this._router.navigate(["head-evaluation/" + cycleId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.HeadEvaluation).processId },
                });
                break;
            case ProcessTypeEnum.EvaluationCalibration:
                const calibrationId = this.processes.find(p => p.processType === ProcessTypeEnum.EvaluationCalibration && p.cycleProcessConfigId === cycleProcessConfigId).processId;
                this._router.navigate([`groups/${cycleId}/${calibrationId}`], { state: { processId: this.processes.find(p => p.processType === ProcessTypeEnum.EvaluationCalibration && p.cycleProcessConfigId === cycleProcessConfigId).processId } });
                break;
            case ProcessTypeEnum.FinalResult:
                this._router.navigate(["final-report/" + cycleId + "/" + this.userInfo.id], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.FinalResult).processId },
                });
                break;
            case ProcessTypeEnum.FinalFeedback:
                this._router.navigate(["final-feedback/" + cycleId + "/" + cycleProcessConfigId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.FinalFeedback).processId },
                });
                break;
            case ProcessTypeEnum.FinalFeedbackValidation:
                this._router.navigate(["final-feedback-validation/" + cycleId + "/" + cycleProcessConfigId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.FinalFeedbackValidation).processId },
                });
                break;
            case ProcessTypeEnum.GenericProcess:
                this._router.navigate(["generic-process/" + cycleId + "/" + cycleProcessConfigId], {
                    state: { processId: this.processes.find((p) => p.processType === ProcessTypeEnum.GenericProcess).processId },
                });
                break;
            default:
                break;
        }
    }

    public async selectCycle() {
        const modal = await this._modalController.create({
            component: SelectCycleComponent,
            cssClass: "select-cycle-modal",
            componentProps: {
                cycles: this.cycles,
                selectedCycleId: this.selectedCycleId,
            },
        });

        modal.onDidDismiss().then((response) => {
            if (response.data) {
                this.selectedCycleId = response.data;
                this.selectedCycle = this.cycles.find((c) => c.cycleId === response.data);
                this._loadSelectedCycle(this.selectedCycleId);
            }
        });

        await modal.present();
    }

    public canAddNewFreeEvaluation(freeEvaluationSummary: FreeEvaluationSummary): boolean {
        if (!freeEvaluationSummary.draftStartDate) return false;

        const Today = new Date();
        const StartDate = new Date(freeEvaluationSummary.draftStartDate);
        const EndDate = new Date(freeEvaluationSummary.endDate);

        return StartDate <= Today && (!freeEvaluationSummary.endDate || EndDate >= Today);
    }

    public canAddNewFreeEvaluationEndYear(summary: FreeEvaluationSummary): boolean {
        if (!summary.endYearSummary || !summary.endYearSummary.draftStartDate) return false;

        const Today = new Date();
        const StartDate = new Date(summary.endYearSummary.draftStartDate);
        const EndDate = new Date(summary.endYearSummary.endDate);

        return StartDate <= Today && (!summary.endYearSummary.endDate || EndDate >= Today);
    }

    public reloadProgresses(): void {
        this._loadGoalsProgress(this.selectedCycleId);
    }

    private _loadCycles(): void {
        this._dashboardService.getUserCycles().subscribe((response) => {
            this.cycles = response.data;
            if (!this._cycleService.getCycle()) {
                if (this.cycles && this.cycles.length > 0) {
                    this.selectedCycleId = this.cycles[0].cycleId;
                    this._cycleService.setCycle(this.selectedCycleId);
                } else this.selectedCycleId = undefined;
            } else this.selectedCycleId = parseInt(this._cycleService.getCycle());

            if (this.cycles && this.cycles.length > 0) {
                this._loadProcesses(this.selectedCycleId);
                this._loadGoalsProgress(this.selectedCycleId);
                this._loadFreeEvaluationSummary(this.selectedCycleId);
            }

            if (this.selectedCycleId && this.cycles && this.cycles.length > 0) {
                this.selectedCycle = this.cycles.find((cycle) => cycle.cycleId === this.selectedCycleId);
            } else if (this.cycles && this.cycles.length > 0) {
                this.selectedCycle = this.cycles[0];
            }
            this.loadedClycle = true;
            this.verifyCycleAttribs();
        });
    }

    private _loadProcesses(cycleId: number): void {
        this._dashboardService.getDashboardProcesses(cycleId).subscribe(
            (response) => {
                this.userAttributes = response.data.userAttributes;
                const processesList = this._removeUnlistedProcesses(response.data.processes);
                this.processes = this._utilService.orderByProperty(processesList, "order");
                this._updateProcessDates();
                const calibrationProcess = this.processes.find((process) => process.processType === ProcessTypeEnum.EvaluationCalibration);
                if (calibrationProcess) this._calibrationProcessId = calibrationProcess.processId;
                this.nextProcess = this._getNextProcess(this.processes);
                this.firstSearchDone = true;
                this._cycleService.setCycle(cycleId);
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _loadGoalsProgress(cycleId: number): void {
        this._dashboardService.getUserGoals(cycleId).subscribe(
            (response) => {
                this.goalsProgress = response.data;
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _loadFreeEvaluationSummary(cycleId: number): void {
        this._dashboardService.getMidYearEvaluationSummary(cycleId).subscribe(
            (response) => {
                this.freeEvaluationSummary = response.data;
            },
            (res) => {
                if (res.error.errors && res.error.errors.length > 0 && res.error.errors[0] !== "cycleProcessConfig.notFound") {
                    this.notify(this.getErrorNotification(res), "danger");
                }
            },
        );
    }

    private _loadPartnerPrinciples(cycleId: number): void {
        this._dashboardService.getPartnerPrinciples(cycleId).subscribe((response) => {
            this.corePrinciplesAllLanguages = response.data.fundamentalPrinciples;
            this.leadershipPrinciplesAllLanguages = response.data.leadershipPrinciples;
            this.fillPartnerPrinciples();
        });
    }

    fillPartnerPrinciples() {
        if (
            this.corePrinciplesAllLanguages &&
            this.corePrinciplesAllLanguages.length > 0 &&
            this.leadershipPrinciplesAllLanguages &&
            this.leadershipPrinciplesAllLanguages.length > 0
        ) {
            this.corePrinciples = this.corePrinciplesAllLanguages.map((principle) => ({
                title: principle[`title${this.currentLanguage}`],
                description: principle[`description${this.currentLanguage}`],
            }));

            this.leadershipPrinciples = this.leadershipPrinciplesAllLanguages.map((principle) => ({
                title: principle[`title${this.currentLanguage}`],
                description: principle[`description${this.currentLanguage}`],
            }));
        }
        return;
    }

    private _getNextProcess(processes: Array<ProcessPreview>): ProcessPreview | undefined {
        return processes.find((process) => process.status === ProcessStatusEnum.WaitingInput || process.status === ProcessStatusEnum.Declined);
    }

    private _removeUnlistedProcesses(processes: Array<ProcessPreview>): Array<ProcessPreview> {
        if (!isNil(processes)) {
            const filteredProcesses: Array<ProcessPreview> = [];
            for (const process of processes) {
                if (process.showOnUserProcessList) {
                    filteredProcesses.push(process);
                }
            }
            return filteredProcesses;
        } else {
            return [];
        }
    }

    private _loadUserInfo(): void {
        this._authService.isLoggedIn().subscribe((isLoggedIn: boolean) => {
            if (isLoggedIn) {
                this._authService.getUserData().subscribe(
                    (response) => {
                        this.userInfo = response.data;
                        this._loadCycles();
                    },
                    (error) => this._authService.logout(),
                );
            } else {
                this.userInfo = undefined;
            }
        });
    }

    private initExternalClientStyles() {
        let loggedUser = (this._authService.getLoggedUser());
        console.log('Applying external client styles');
        applyExternalClientStyles(
            this._attributeService,
            this._externalClientStylesService,
            this._logoService,
            this._authService,
            loggedUser.user_id
        );
    }
    private initCachedExternalClientStyles() {
        console.log('Applying cached external client styles');
        applyCachedExternalClientStyles(
            this._logoService,
            this._authService,
            this.userInfo?.id
        );
    }

    private _loadSelectedCycle(cycleId: number): void {
        this.selectedCycle = this.cycles.find((cycle) => cycle.cycleId === cycleId);
        this.verifyCycleAttribs();

        if (this.firstSearchDone) {
            this._loadProcesses(cycleId);
            this._loadGoalsProgress(cycleId);
        }
    }

    private _loadPrinciples(): void {
        if (!this.selectedCycle || !this.selectedCycle.isCyclePartner) {
            let selectedLanguage = this._translationService.getSelectedLanguage();
            this._dashboardService.getPrinciples(selectedLanguage).subscribe((response) => {
                let data = response.data;
                if (data) {
                    this.corePrinciples = response.data['core'];
                    this.leadershipPrinciples = response.data['leadership'];
                }
            });
        }
    }

    private _updateProcessDates(): void {
        this.processes.forEach((process) => {
            process.startsAtString = this._utilService.convertDateString(process.startsAt, false, true);
            process.endsAtString = this._utilService.convertDateString(process.endsAt, false, true);
        });
    }
}
